import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, Bell, Shield, CreditCard, CheckCircle2, BarChart3, Mail, Plus, Edit2, Trash2, Star } from 'lucide-react';
import api from '../../services/api';
import { useAuthStore } from '../../stores/authStore';

const Settings = () => {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState('company');
  const [verificationSubTab, setVerificationSubTab] = useState('sources');

  // State for all verification sources
  const [dvlaEnabled, setDvlaEnabled] = useState(false);
  const [dvlaTestMode, setDvlaTestMode] = useState(true);
  const [passportEnabled, setPassportEnabled] = useState(false);
  const [electoralEnabled, setElectoralEnabled] = useState(false);
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [linkedInEnabled, setLinkedInEnabled] = useState(false);
  const [openBankingEnabled, setOpenBankingEnabled] = useState(false);

  // State for templates
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    body: '',
    isDefault: false
  });
  const [billingPlan, setBillingPlan] = useState('PROFESSIONAL'); // Default to PROFESSIONAL for now

  // State for profile form
  const [profileForm, setProfileForm] = useState({
    company_name: '',
    email: '',
    phone: '',
    address: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // State for invite member
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'FIRM_USER' });
  const [inviting, setInviting] = useState(false);

  // State for password change
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [updatingPassword, setUpdatingPassword] = useState(false);

  const tabs = [
    { id: 'company', label: 'Company Profile', icon: Building2 },
    { id: 'users', label: 'Team Members', icon: Users },
    { id: 'templates', label: 'Email Templates', icon: Mail },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'billing', label: 'Billing', icon: CreditCard },
  ];

  const verificationTabs = [
    { id: 'sources', label: 'Configure Sources' },
    { id: 'comparison', label: 'Compare All Sources' }
  ];

  // Load templates and company info
  useEffect(() => {
    if (activeTab === 'templates') {
      fetchTemplates();
      fetchCompanyInfo();
    }
  }, [activeTab]);

  const fetchCompanyInfo = async () => {
    try {
      if (user?.company?.slug) {
        const response = await api.get(`/firms/${user.company.slug}`);
        const data = response.data;
        setBillingPlan(data.firm?.billing_plan || 'FREE');
      }
    } catch (error) {
      console.error('Failed to fetch company info:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      subject: '{{companyName}} has requested verification',
      body: 'Dear #firstname #surname,\n\n{{companyName}} has requested verification through Acquiva.\n\nPlease click the link below to complete your verification:\n\n#verificationlink\n\nThis link will expire in 14 days.\n\nIf you have any questions, please contact {{companyName}} directly.\n\nBest regards,\nThe Acquiva Team',
      isDefault: false
    });
    setShowTemplateModal(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      body: template.body,
      isDefault: template.is_default
    });
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async () => {
    try {
      if (editingTemplate) {
        await api.put(`/templates/${editingTemplate.id}`, templateForm);
      } else {
        await api.post('/templates', templateForm);
      }
      setShowTemplateModal(false);
      fetchTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await api.delete(`/templates/${templateId}`);
      fetchTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
    }
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById('template-body');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = templateForm.body;
    const before = text.substring(0, start);
    const after = text.substring(end);

    setTemplateForm({
      ...templateForm,
      body: before + variable + after
    });

    // Set cursor position after inserted variable
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      await api.put('/firms/profile', profileForm);
      alert('Profile updated successfully');
    } catch (err) {
      console.error('Save profile error:', err);
      alert(err.response?.data?.error?.message || 'Failed to save profile. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleInviteMember = () => {
    setInviteForm({ email: '', role: 'FIRM_USER' });
    setShowInviteModal(true);
  };

  const handleSendInvite = async () => {
    if (!inviteForm.email) {
      alert('Please enter an email address');
      return;
    }

    try {
      setInviting(true);
      await api.post('/firms/invite', inviteForm);
      alert('Invitation sent successfully');
      setShowInviteModal(false);
      setInviteForm({ email: '', role: 'FIRM_USER' });
    } catch (err) {
      console.error('Invite error:', err);
      alert(err.response?.data?.error?.message || 'Failed to send invitation. Please try again.');
    } finally {
      setInviting(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      alert('Please fill in all password fields');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      alert('New password must be at least 8 characters long');
      return;
    }

    try {
      setUpdatingPassword(true);
      await api.put('/users/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      alert('Password updated successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      console.error('Update password error:', err);
      alert(err.response?.data?.error?.message || 'Failed to update password. Please try again.');
    } finally {
      setUpdatingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">Manage your company settings and preferences</p>
      </div>

      <div className="flex space-x-8 border-b border-gray-200">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {activeTab === 'company' && (
          <div className="space-y-6 max-w-2xl">
            <h2 className="text-lg font-semibold text-gray-900">Company Profile</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name
              </label>
              <input
                type="text"
                defaultValue="Acme Legal Ltd"
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Website
              </label>
              <input
                type="url"
                defaultValue="https://acmelegal.com"
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <textarea
                rows={3}
                defaultValue="123 Legal Street&#10;London, EC1A 1BB&#10;United Kingdom"
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Number
              </label>
              <input
                type="text"
                defaultValue="12345678"
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div className="pt-4">
              <button
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingProfile ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
              <button
                onClick={handleInviteMember}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
              >
                Invite Member
              </button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium mr-4">
                    AU
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Admin User</p>
                    <p className="text-sm text-gray-500">admin@acquiva.co</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  Admin
                </span>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-medium mr-4">
                    JR
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">John Reviewer</p>
                    <p className="text-sm text-gray-500">john@acquiva.co</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                  Reviewer
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Verification Sources tab removed - now managed at platform level */}

        {activeTab === 'templates' && (
          <div className="space-y-6">
            {/* Plan restriction banner for non-Professional plans */}
            {billingPlan !== 'PROFESSIONAL' && billingPlan !== 'ENTERPRISE' && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Custom Email Templates - Professional Feature
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Create and manage custom email templates for verification invites. This feature is available on Professional and Enterprise plans.
                    </p>
                    <Link
                      to="/pricing"
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                    >
                      Upgrade to Professional
                    </Link>
                  </div>
                </div>
              </div>
            )}

            <div className={billingPlan !== 'PROFESSIONAL' && billingPlan !== 'ENTERPRISE' ? 'opacity-40 pointer-events-none' : ''}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Email Templates</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Create and manage email templates for verification invites
                  </p>
                </div>
                <button
                  onClick={handleCreateTemplate}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  disabled={billingPlan !== 'PROFESSIONAL' && billingPlan !== 'ENTERPRISE'}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Template
                </button>
              </div>
            </div>

            <div className={`space-y-4 ${billingPlan !== 'PROFESSIONAL' && billingPlan !== 'ENTERPRISE' ? 'opacity-40 pointer-events-none' : ''}`}>
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:border-blue-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                        {template.is_default && (
                          <span className="flex items-center px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                            <Star className="w-3 h-3 mr-1" />
                            Default
                          </span>
                        )}
                        {template.is_system && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                            System
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        <strong>Subject:</strong> {template.subject}
                      </p>
                      <div className="bg-gray-50 rounded p-3 text-sm text-gray-700 font-mono whitespace-pre-wrap max-h-32 overflow-y-auto">
                        {template.body.substring(0, 200)}
                        {template.body.length > 200 && '...'}
                      </div>
                      <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                        <span>Used {template.usage_count} times</span>
                        {template.last_used_at && (
                          <span>Last used: {new Date(template.last_used_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleEditTemplate(template)}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {!template.is_system && (
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {templates.length === 0 && (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Mail className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No templates yet. Create your first template!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Billing</h2>

            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-600">Billing information will be displayed here.</p>
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Security</h2>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="font-medium text-gray-900 mb-4">Change Password</h3>
              <div className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="pt-4">
              <button
                onClick={handleUpdatePassword}
                disabled={updatingPassword}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updatingPassword ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Template Editor Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingTemplate ? 'Edit Template' : 'Create New Template'}
              </h2>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name
                </label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="e.g., Default Invite, Urgent Request"
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Subject
                </label>
                <input
                  type="text"
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                  placeholder="{{companyName}} has requested verification"
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Email Body
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => insertVariable('#firstname')}
                      className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                    >
                      + First Name
                    </button>
                    <button
                      type="button"
                      onClick={() => insertVariable('#surname')}
                      className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                    >
                      + Surname
                    </button>
                    <button
                      type="button"
                      onClick={() => insertVariable('#verificationlink')}
                      className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                    >
                      + Link
                    </button>
                  </div>
                </div>
                <textarea
                  id="template-body"
                  value={templateForm.body}
                  onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                  rows={12}
                  placeholder="Dear #firstname #surname,&#10;&#10;{{companyName}} has requested verification...&#10;&#10;#verificationlink"
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 font-mono text-sm"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Available variables: <code className="bg-gray-100 px-1 rounded">#firstname</code>,{' '}
                  <code className="bg-gray-100 px-1 rounded">#surname</code>,{' '}
                  <code className="bg-gray-100 px-1 rounded">#verificationlink</code>,{' '}
                  <code className="bg-gray-100 px-1 rounded">{'{{companyName}}'}</code>
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is-default"
                  checked={templateForm.isDefault}
                  onChange={(e) => setTemplateForm({ ...templateForm, isDefault: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="is-default" className="ml-2 text-sm text-gray-700">
                  Set as default template
                </label>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowTemplateModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTemplate}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {editingTemplate ? 'Update Template' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Invite Team Member</h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="colleague@example.com"
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="FIRM_USER">Company User</option>
                  <option value="FIRM_ADMIN">Company Admin</option>
                </select>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowInviteModal(false)}
                disabled={inviting}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvite}
                disabled={inviting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {inviting ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
